import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { API_ENDPOINTS } from '../constants/api';
import { toProperCase } from '../utils/textUtils';
import './FarmModal.css';

interface Farm {
  farm_id: string;
  farm: string;
  business_id: string;
  is_current?: boolean;
}

interface FarmModalProps {
  farm: Farm | null;
  businessId: string;
  isOpen: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (farm: Farm) => void;
  onDelete?: (farmId: string) => void;
  userToken: string;
  existingFarms?: Farm[]; // For duplicate validation
  selectedOrganizationId?: string; // For super users
}

const FarmModal: React.FC<FarmModalProps> = ({
  farm,
  businessId,
  isOpen,
  isEdit,
  onClose,
  onSave,
  onDelete,
  userToken,
  existingFarms = [],
  selectedOrganizationId,
}) => {
  const [formData, setFormData] = useState<Farm>({
    farm_id: '',
    farm: '',
    business_id: businessId,
    is_current: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  // Update form data when farm changes (for edit mode)
  useEffect(() => {
    if (farm && isEdit) {
      setFormData({
        farm_id: farm.farm_id,
        farm: farm.farm,
        business_id: farm.business_id,
        is_current: farm.is_current !== undefined ? farm.is_current : true
      });
    } else {
      // Reset form for add mode
      setFormData({
        farm_id: '',
        farm: '',
        business_id: businessId,
        is_current: true
      });
    }
    
    // Reset delete confirmation state when modal opens/closes or mode changes
    setShowDeleteConfirm(false);
  }, [farm, isEdit, businessId, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox for is_current
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    
    // Apply proper case conversion to farm name
    const processedValue = name === 'farm' ? toProperCase(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      // Check for duplicate farm name within the same business unit (only for create, not edit)
      if (!isEdit) {
        const trimmedFarmName = formData.farm.trim();
        const duplicateFarm = existingFarms.find(existingFarm => 
          existingFarm.business_id === businessId && 
          existingFarm.farm.toLowerCase() === trimmedFarmName.toLowerCase()
        );

        if (duplicateFarm) {
          setError(`A farm named "${trimmedFarmName}" already exists in this business unit`);
          setIsSaving(false);
          return;
        }
      }

      // Add organization_id parameter for Super Users when creating
      const organizationParam = !isEdit && selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
      const url = isEdit 
        ? `${API_ENDPOINTS.FARMS}/${farm?.farm_id}`
        : `${API_ENDPOINTS.FARMS}${organizationParam}`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      // For create, send the farm name, business_id, and is_current
      // For update, send the farm name and is_current
      const payload = isEdit 
        ? { farm: formData.farm, is_current: formData.is_current }
        : { farm: formData.farm, business_id: formData.business_id, is_current: formData.is_current };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} farm`);
      }

      const savedFarm = await response.json();
      onSave(savedFarm);
      onClose();
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} farm:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} farm`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setError('');
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!farm || !onDelete) return;
    
    setIsDeleting(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.FARMS}/${farm.farm_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete farm');
      }

      onDelete(farm.farm_id);
      onClose();
    } catch (err) {
      console.error('Error deleting farm:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete farm');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  if (!isOpen) {
    return null;
  }

  // If showing delete confirmation, only show that modal
  if (showDeleteConfirm) {
    return (
      <div className="modal-overlay">
        <div className="modal-content delete-confirm-modal">
          <div className="modal-header">
            <h2>Confirm Delete</h2>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this farm?</p>
            <p className="warning-text">
              <strong>Warning:</strong> This will also delete all associated fields.
            </p>
          </div>
          <div className="modal-actions">
            <button 
              type="button" 
              onClick={handleDeleteCancel}
              className="btn-secondary"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleDeleteConfirm}
              className="btn-delete"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show the edit/create modal
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Farm' : 'Add New Farm'}</h2>
          <button className="modal-close" onClick={handleCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form" autoComplete="off">
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="farm">Farm Name *</label>
            <input
              type="text"
              id="farm"
              name="farm"
              value={formData.farm}
              onChange={handleInputChange}
              required
              placeholder="Enter farm name"
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_current"
                checked={formData.is_current}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">Currently Active</span>
            </label>
          </div>

          <div className="modal-actions">
            {isEdit && farm && (
              <button 
                type="button" 
                onClick={handleDeleteClick}
                className="btn-delete"
                disabled={isSaving || isDeleting}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            )}
            <button 
              type="button" 
              onClick={handleCancel}
              className="btn-secondary"
              disabled={isSaving || isDeleting}
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleSubmit}
              className="btn-primary"
              disabled={isSaving || isDeleting}
            >
              {isSaving ? 'Saving...' : (isEdit ? 'Update Farm' : 'Create Farm')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FarmModal; 